import type { AbstractPowerSyncDatabase, PowerSyncBackendConnector } from '@powersync/web';
import type { PowerSyncCredentials as BridgeCredentials } from '@helios/powersync-auth-bridge-web';

export type GetCredentialsFn = () => Promise<BridgeCredentials>;

/**
 * PowerSync connector that uses Firebase ID tokens for authentication.
 *
 * When a `writeUrl` is provided, local CRUD transactions are POSTed to the
 * syncWrite Firebase Function. Otherwise, transactions are completed locally
 * without upload (backward-compatible with Phase 1).
 */
export class FirebasePowerSyncConnector implements PowerSyncBackendConnector {
  private getCredentials: GetCredentialsFn;
  private writeUrl: string | undefined;

  constructor(getCredentials: GetCredentialsFn, writeUrl?: string) {
    this.getCredentials = getCredentials;
    this.writeUrl = writeUrl;
  }

  async fetchCredentials() {
    console.log('[PowerSync Connector] fetchCredentials called');
    try {
      const credentials = await this.getCredentials();
      console.log('[PowerSync Connector] Credentials fetched:', {
        endpoint: credentials.endpoint,
        hasToken: !!credentials.token,
        tokenLength: credentials.token?.length,
      });
      return credentials;
    } catch (err) {
      console.error('[PowerSync Connector] Failed to fetch credentials:', err);
      throw err;
    }
  }

  async uploadData(database: AbstractPowerSyncDatabase): Promise<void> {
    console.log('[PowerSync] uploadData() called', { writeUrl: this.writeUrl });

    const transaction = await database.getNextCrudTransaction();
    if (!transaction) {
      console.log('[PowerSync] uploadData: no pending transaction');
      return;
    }

    console.log('[PowerSync] uploadData: got transaction', {
      crudCount: transaction.crud.length,
      transactionId: transaction.transactionId,
      ops: transaction.crud.map((e) => ({
        op: e.op,
        type: e.table,
        id: e.id,
        dataKeys: e.opData ? Object.keys(e.opData) : [],
      })),
    });

    // No write URL configured — discard silently (Phase 1 / local-only mode)
    if (!this.writeUrl) {
      console.warn(
        '[PowerSync] No writeUrl configured. Completing transaction without upload.',
        transaction.crud.length,
        'ops discarded.',
      );
      await transaction.complete();
      return;
    }

    const MAX_RETRIES = 4;
    const RETRY_DELAYS = [3000, 5000, 8000, 12000]; // backoff: 3s, 5s, 8s, 12s

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const credentials = await this.getCredentials();

        const payload = { operations: transaction.crud.map((e) => e.toJSON()) };
        if (attempt === 0) {
          console.log('[PowerSync] uploadData: payload', JSON.stringify(payload, null, 2));
        }

        const body = JSON.stringify(payload);

        console.log(
          '[PowerSync] uploadData: POSTing to',
          this.writeUrl,
          attempt > 0 ? `(retry ${attempt})` : '',
        );
        const res = await fetch(this.writeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${credentials.token}`,
          },
          body,
        });

        const text = await res.text();
        console.log('[PowerSync] uploadData: response', { status: res.status, body: text });

        if (!res.ok) {
          throw new Error(`syncWrite responded ${res.status}: ${text}`);
        }

        await transaction.complete();
        console.log('[PowerSync] Upload completed:', transaction.crud.length, 'ops sent.');
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRetryable =
          msg === 'Failed to fetch' ||
          msg === 'Load failed' ||
          msg.includes('busy re-opening') ||
          msg.includes('network') ||
          msg.includes('ECONNREFUSED') ||
          msg.includes('timeout');

        if (isRetryable && attempt < MAX_RETRIES) {
          const delay = RETRY_DELAYS[attempt];
          console.warn(
            `[PowerSync] Upload failed (attempt ${attempt + 1}/${MAX_RETRIES + 1}), retrying in ${delay}ms...`,
            msg,
          );
          await new Promise((r) => setTimeout(r, delay));
          continue;
        }

        console.error('[PowerSync] Upload failed after all retries:', err);
        throw err;
      }
    }
  }
}
